import React from 'react'
import { useTranslation } from 'react-i18next'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Grid,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { chunk, isEmpty, isNull, map } from 'lodash'
import PropTypes from 'prop-types'

import { PointChecked, PointUnchecked, WarnIcon } from '../../icons'
import Legend from '../Legend'

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '& td, & th': {
    border: 0,
  },
}))

function DigitalCertificateCarControl({ certificateData }) {
  const { technicalConditions } = certificateData
  const theme = useTheme()
  const matches = useMediaQuery(theme.breakpoints.up('md'))
  const { t } = useTranslation()

  const legendItems = [
    {
      icon: <PointChecked />,
      label: t('carControl.status.ok'),
      iconColor: 'success',
    },
    {
      icon: <WarnIcon />,
      label: t('carControl.status.notOk'),
      iconColor: 'warning',
    },
    {
      icon: <PointUnchecked />,
      label: t('carControl.status.notApplicable'),
    },
  ]

  return isNull() ? null : (
    <>
      {map(technicalConditions.getTechnicalConditionGroupModel, (group, gk) => {
        const groupElemsColumns = matches
          ? chunk(
              group.technicalChecklistGroupElements,
              Math.ceil(group.technicalChecklistGroupElements.length / 2),
            )
          : [group.technicalChecklistGroupElements]
        return (
          <Accordion
            key={gk}
            elevation={0}
            sx={({ palette }) => ({
              backgroundColor: palette.background.default,
            })}
          >
            <AccordionSummary
              aria-controls={`panel${gk}a-content`}
              id={`panel${gk}a-header`}
              expandIcon={<ExpandMoreIcon />}
              sx={{ p: 0 }}
            >
              <Typography variant="h3">
                {group.technicalChecklistGroupName}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <Grid container spacing={5}>
                <Grid item xs={12}>
                  <Box sx={{ ml: 2 }}>
                    <Legend items={legendItems} orientation="vertical" />
                  </Box>
                </Grid>
                {map(groupElemsColumns, (column, ck) => (
                  <Grid key={ck} item xs={12} md={6}>
                    <Table>
                      <TableBody>
                        {map(column, (item, ik) => (
                          <StyledTableRow key={ik}>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ display: 'flex', alignItems: 'strech' }}
                              >
                                <Box
                                  component="span"
                                  sx={({ palette }) => ({
                                    color:
                                      (item.technicalConditionStatus === 'Ok' &&
                                        palette.success.main) ||
                                      (item.technicalConditionStatus ===
                                        'NotOk' &&
                                        palette.warning.main) ||
                                      (item.technicalConditionStatus ===
                                        'NotApplicable' &&
                                        palette.text.disabled),
                                  })}
                                >
                                  {item.technicalConditionStatus === 'Ok' && (
                                    <PointChecked />
                                  )}
                                  {item.technicalConditionStatus ===
                                    'NotOk' && <WarnIcon />}
                                  {item.technicalConditionStatus ===
                                    'NotApplicable' && <PointUnchecked />}
                                </Box>
                                <Box component="span" sx={{ ml: 1 }}>
                                  {item.technicalChecklistItemName}
                                  <br />
                                  {item.technicalConditionStatus === 'NotOk' &&
                                    !isEmpty(item.description) && (
                                      <Typography
                                        variant="caption"
                                        sx={({ palette }) => ({
                                          color: palette.text.disabled,
                                        })}
                                      >
                                        {item.description}
                                      </Typography>
                                    )}
                                </Box>
                              </Typography>
                            </TableCell>
                          </StyledTableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        )
      })}
      <Divider sx={{ mb: 2 }} />
      <Typography variant="h3" gutterBottom>
        {t('digital.checklist.repairs')}
      </Typography>
      <Paper elevation={0} sx={{ p: 2 }}>
        <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
          {technicalConditions.description}
        </Typography>
      </Paper>
    </>
  )
}

DigitalCertificateCarControl.propTypes = {
  certificateData: PropTypes.object,
}
DigitalCertificateCarControl.defaultProps = {
  certificateData: null,
}

export default DigitalCertificateCarControl
